/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2014, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'../lib/@waca/core-client/js/core-client/ui/core/View',
	'jquery',
	'underscore',
	'../lib/@waca/dashboard-common/dist/ui/CenterLoadingSpinner',
	'./PinsPanelView',
	'../app/nls/StringResources',
	'../app/util/CapabilityUtils',
	'react',
	'react-dom'
], function (
	BaseView,
	$,
	_,
	CenterLoadingSpinner,
	PinsPanelView,
	StringResources,
	CapabilityUtils,
	React,
	ReactDOM
) {

	/**
	 * The Panel which shows the pinned content
	 */
	const View = BaseView.extend({

		events: {
			'mousedown .pinItem :not(button)': 'onDragStart',
			'mouseup .pinItem :not(button)': 'onMouseUp'
		},

		init: function (attributes) {
			View.inherited('init', this, arguments);
			this.ajaxSvc = attributes.ajaxSvc;
			this.glassContext = attributes.glassContext;
			this.canvasController = attributes.canvasController;
			this.dashboardApi = this.canvasController.dashboardApi;
			this.logger = this.glassContext.getCoreSvc('.Logger');

			this.dndManager = attributes.dndManager;
			this.dashboardPinningService = attributes.dashboardPinningService;
			this.smartNamingSvc = attributes.smartNamingSvc;

			this.viewMode = attributes.viewMode;
			this.dateFilterString = 'all';

			if (!this.dashboardPinningService ) {
				throw new Error('Pinning service not provided to PinsPanel constructor');
			}
		},

		/**
		 * Called by the DnD manager to check if this drop zone accept the dragged object
		 *
		 * @returns {Boolean}
		 */
		accepts: function (dragObject) {
			return dragObject.type === 'pin';
		},

		/**
		 * Handles dragging action.
		 * The function sends the id of a pin to pinning service and calls the dnd manager with the pin spec result
		 *
		 * @public
		 *
		 * @param  {object} event - The event to process
		 */
		onDragStart: function (event) {
			this._mouseUp = false;
			this._mouseSession = {};
			const mouseSession = this._mouseSession;

			if (this.dashboardApi.getMode() !== this.dashboardApi.MODES.EDIT) {
				return Promise.resolve(true);
			}

			if (this._shouldPreventPinDrag(event)) {
				return Promise.resolve();
			}

			const pinEl = $(event.currentTarget).parents('.pinItem')[0];
			const id = pinEl && pinEl.getAttribute('data-id');
			if (!id) {
				return Promise.resolve();
			}

			return this.getPin(id)
				.then((pinSpec) => {
					// If the mouse is up before we start dragging then just return
					if (this._mouseUp) {
						return;
					}
					return JSON.parse(JSON.stringify(pinSpec));
				})
				.then((pinSpec) => {
					return this._canAddPin(mouseSession, pinSpec)
						.then((pinSpec) => {
							pinSpec && this.dndManager.startDrag({
								event: event,
								type: 'pin',
								data: this._buildDropInfo(pinSpec),
								avatar: this._buildAvatar(pinSpec),
								moveXThreshold: 20,
								moveYThreshold: 20
							});
						});
				})
				.catch((err) => {
					this.dashboardApi.getFeature('Logger').error('Fail to drag pin into canvas', err);
					throw err;
				});
		},

		_canAddPin(mouseSession, pinSpec) {
			if (!mouseSession.capabilityIsChecked) {
				mouseSession.capabilityIsChecked = CapabilityUtils.checkAuthoringCapability(this.dashboardApi, pinSpec.content, StringResources.get('noCapabilityForPin'));
			}
			return mouseSession.capabilityIsChecked
				.then((hasCapability) => {
					if (hasCapability && !_.isEmpty(pinSpec)) {
						return pinSpec;
					}
				});
		},

		// User might of single clicked, so stop the drag from starting
		onMouseUp: function () {
			this._mouseUp = true;
		},

		_shouldPreventPinDrag(event) {
			return $(event.target).closest('button').length || event.target.tagName === 'INPUT';
		},

		/**
		 * Gets a pin spec from calling glassContext using a pin id
		 *
		 * @public
		 *
		 * @param {stirng} id - The id of a pin
		 * @return {string} pinSpec - The spec of a pin
		 */
		getPin: function (id) {
			return this.dashboardPinningService.getPin(id);
		},

		/**
		 * A helper function to build avatar for a pin
		 * @param  {string} pinSpec - the spec of a pin
		 * @return {object} - a jquery object with an img of the pin if present.
		 */
		_buildAvatar: function (pinSpec) {
			var avatar = $('<img>', {
				class: 'avatar pin'
			});
			avatar.attr('src', pinSpec.thumbUri);
			return avatar;
		},

		/**
		 * a helper method to build drop info based on the pin spec that was received from the server
		 *
		 * @private
		 *
		 * @param  {object} pinSpec - description of a pin
		 * @return {object} dropInfo
		 */
		_buildDropInfo: function (pinSpec) {
			return {
				operation: 'new',
				pinSpec: pinSpec
			};
		},


		/**
		 * primary action handler for adding pin to canvas
		 *
		 * @public
		 * @param {string} id - id of a pin to add to canvas
		 * @param {event} event - The event object to process
		 */
		onKeyDownToCreatePin: function (pinId, event) {
			if (event.key && event.keycode === 13) {
				// select pin by navigation keyboard (and not bubbles from click)
				this._mouseSession = {};
			}
			const mouseSession = this._mouseSession;
			// get selected pin
			if (!pinId) {
				return Promise.resolve();
			}

			return this.getPin(pinId)
				.then((pinSpec) => {
					return JSON.parse(JSON.stringify(pinSpec)); //Clone the pin spec, as the add operation is side-effecting, and we don't want any updates to change the cached pin.
				})
				.then((pinSpec) => this._canAddPin(mouseSession, pinSpec))
				.then((pinSpec) => {
					pinSpec && this._addPin(pinSpec, event);
				})
				.catch((err) => {
					this.dashboardApi.getFeature('Logger').error('Fail to create pin from keyboard navigation', err);
					throw err;
				});
		},

		_addPin(pinSpec, event) {
			const pinContent = pinSpec.content;
			let fragmentModel = {
				layout: pinContent.layout,
				widgets: pinContent.widgets,
				dataSources: pinContent.dataSources,
				drillThrough: pinContent.drillThrough,
				version: pinSpec.version ? pinContent.specVersion : 6,
				sourceName: pinSpec.sourceName
			};
			if (pinContent.episodes) {
				fragmentModel.episodes = pinContent.episodes;
			}
			let options = {
				model: fragmentModel
			};
			if (pinContent.layout && pinContent.layout.style) {
				options.layoutProperties = {
					style: _.omit(pinContent.layout.style, 'top', 'left')
				};
			}
			const isTouch = event.type === 'tap';
			this._addPinToLayout(options, isTouch);
		},

		_addPinToLayout: function(options, isTouch) {
			this.canvasController.addPin(options, isTouch);
		},

		render: function() {
			return this._renderSpinner().then(() => {
				this.dndManager.addDropTarget(this.el, {
					accepts: this.accepts
				});
				ReactDOM.render(
					<PinsPanelView
						addPinToCanvas={(pinId, event) => { this.onKeyDownToCreatePin(pinId, event);}}
						smartNamingSvc={this.smartNamingSvc}
						dashboardPinningService={this.dashboardPinningService}
					/>, this.$el[0]);
			});
		},

		_renderSpinner(){
			const spinnerContainer = document.createElement('div');
			this.$el.append(spinnerContainer);
			const spinnerOptions = { variant: 'circle', size: 'normal' };
			ReactDOM.render(CenterLoadingSpinner(spinnerOptions), spinnerContainer);
			return this.dashboardPinningService.getPins(this.dateFilterString).then(() => ReactDOM.unmountComponentAtNode(spinnerContainer));
		},

		remove() {
			ReactDOM.unmountComponentAtNode(this.$el[0]);
			View.inherited('remove', this, arguments);
		}
	});

	return View;
});
